import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  MutateOptions,
  MutationFunction,
  MutationKey,
  MutationStatus,
  QueryFunction,
  QueryKey,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { GenericErrorResponse } from '../api';
import { fallbackEmptyString } from '../utils/StringUtils';
import { ServiceError, useError } from './error';
import { usePrevious } from './usePrevious';
import { useUserDetails } from './userDetails';

/**
 * Extra fields to react-query query returns
 */
export interface ReactQuertReturnExtra {
  /**
   * Return customized error message
   */
  _error: string;
  /**
   * Indicate the api is authorized
   */
  _authorized: boolean;
}

/**
 * Custom query return that extend the type with custom fields
 */
export declare type CustomQueryReturn<TData, TError> = UseQueryResult<TData, TError> &
  ReactQuertReturnExtra;

/**
 * A wrapper to wrap React-Query useQuery hook to provide centralized error handling
 * @param key useQuery key
 * @param queryFn useQuery function to call api
 * @param options useQuery options
 * @param isSession Indicate the query is session guarded
 * @param isRedirect Indicate the query should allow redirect handling if error occurs
 * @returns
 */
export function useCustomQuery<TQueryFnData = unknown, TData = TQueryFnData>(
  key: QueryKey,
  queryFn: QueryFunction<TQueryFnData>,
  options?: UseQueryOptions<TQueryFnData, ServiceError, TData>,
  isSession = true,
  isRedirect = true
): CustomQueryReturn<TData, ServiceError> {
  const error = useError();
  const resp = useQuery<TQueryFnData, ServiceError, TData>(key, queryFn, options);
  // eslint-disable-next-line no-underscore-dangle
  let _authorized = true;
  if (isSession) {
    // eslint-disable-next-line no-underscore-dangle
    _authorized = error.session(resp.error, isRedirect);
  }
  // eslint-disable-next-line no-underscore-dangle
  const _error = error.api(resp.error, isRedirect);

  return {
    ...resp,
    _error,
    _authorized,
  };
}

/**
 * Extra fields to react-query mutation returns
 */
export interface ReactQueryMutationReturnExtra {
  /**
   * Return customized error message
   */
  _error: string;
  /**
   * Indicate the api is authorized
   */
  _authorized: boolean;
  /**
   * Return extra mutation success count, currently library only provide failure count
   */
  _successCount: number;
  /**
   * Wrapper reset function, to reset _successCount as well
   */
  _reset: () => void;
}

/**
 * Custom mutation return that extend the type with custom fields
 */
export declare type CustomMutateReturn<TData, TError, TVariables, TContext> = UseMutationResult<
  TData,
  TError,
  TVariables,
  TContext
> &
  ReactQueryMutationReturnExtra;

/**
 * A wrapper to wrap React-Query useMutation hook to provide centralized error handling
 * @param key useMutation key
 * @param mutationFn useMutation function to call api
 * @param options useMutation options
 * @param isSession Indicate the mutation is session guarded
 * @param isRedirect Indicate the mutation should allow redirect handling if error occurs
 * @returns
 */
export function useCustomMutate<
  TData = unknown,
  TVariables = void,
  TError = GenericErrorResponse,
  TContext = unknown
>(
  key: MutationKey,
  mutationFn: (csrf: string, variables: TVariables) => Promise<TData>,
  options?: UseMutationOptions<TData, AxiosError<TError>, TVariables, TContext>,
  isSession = true,
  isRedirect = true
): CustomMutateReturn<TData, AxiosError<TError>, TVariables, TContext> {
  const [mutationSuccessCount, setMutationSuccessCount] = useState(0);

  const error = useError<TError>();

  const tempOptions = { ...options };
  delete tempOptions.onSuccess;

  const { isFetching, data: userDetailData } = useUserDetails();

  // enchancer to cast crsfToken as second param after variables
  const enchancedMutationFn: MutationFunction<TData, TVariables> = useCallback(
    (variables: TVariables) =>
      mutationFn(fallbackEmptyString(userDetailData?.data?.csrfToken), variables),
    [userDetailData?.data?.csrfToken, mutationFn]
  );

  const resp = useMutation<TData, AxiosError<TError>, TVariables, TContext>(
    key,
    enchancedMutationFn,
    {
      ...tempOptions,
      // eslint-disable-next-line consistent-return
      onSuccess: (...pars) => {
        setMutationSuccessCount(mutationSuccessCount + 1);
        if (options?.onSuccess) {
          const ret = options.onSuccess(...pars);
          if (ret instanceof Promise) {
            return ret;
          }
          return undefined;
        }
        return undefined;
      },
    }
  );
  // eslint-disable-next-line no-underscore-dangle
  let _authorized = true;
  if (isSession) {
    // eslint-disable-next-line no-underscore-dangle
    _authorized = error.session(resp.error, isRedirect);
  }
  // eslint-disable-next-line no-underscore-dangle
  const _error = error.api(resp.error, isRedirect);

  // csrfLogic
  const [mutating, setMutating] = useState(false);
  const [formData, setFormData] = useState<TVariables | null>(null);
  const [formDataOption, setFormDataOption] = useState<
    MutateOptions<TData, AxiosError<TError>, TVariables, TContext> | undefined
  >(undefined);

  const { mutate, status: mutationStatus } = resp;

  const postMutate = useCallback(
    (
      data: TVariables,
      postMutateOptions?: MutateOptions<TData, AxiosError<TError>, TVariables, TContext>
    ) => {
      setFormData(data);
      setFormDataOption(postMutateOptions);
      if (isFetching) {
        setMutating(true);
      } else {
        mutate(data, postMutateOptions);
      }
    },
    [isFetching, mutate]
  );

  const customStatus = useMemo(() => {
    if (isFetching && mutating) {
      return 'loading' as MutationStatus;
    }
    return mutationStatus;
  }, [isFetching, mutating, mutationStatus]);

  useEffect(() => {
    if (!isFetching && mutating && !!userDetailData?.data?.csrfToken && formData !== null) {
      postMutate(formData, formDataOption);
      setMutating(false);
    }
  }, [userDetailData?.data?.csrfToken, formData, isFetching, mutating, postMutate, formDataOption]);

  const tempResp = { ...resp };
  tempResp.mutate = postMutate;
  tempResp.status = customStatus;
  return {
    ...tempResp,
    _error,
    _authorized,
    _successCount: mutationSuccessCount,
    _reset: () => {
      setMutationSuccessCount(0);
      resp.reset();
    },
  };
}

const getEnabledForPostQuery = (
  isCsrfTokenPresent: boolean,
  isUserDetailsQueryError: boolean,
  postQueryEnabled = true
) => {
  let isEnable = postQueryEnabled;

  // if csrf token is not present i.e. get user details query is loading, do not enable post query,
  // but proceed with post query when user details query returned error, because backend will throw error for post without csrf token
  if (!isCsrfTokenPresent && !isUserDetailsQueryError) {
    isEnable = false;
  }

  return isEnable;
};

declare type UseCustomPostQueryReturn<TData, ServiceError> = CustomQueryReturn<
  TData,
  ServiceError
> & {
  _refetch: () => void;
};

/**
 * Use this query to handle HTTP POST query. It will insert csrf token into the query function provided.
 *
 * @param key
 * @param queryFn
 * @param requestParams
 * @param options
 * @param isSession
 * @param isRedirect
 */
// overload typescript function definition where requestParams is not optional
export function useCustomPostQuery<
  TQueryFnData = unknown,
  TRequestParams = unknown,
  TData = TQueryFnData
>(
  key: QueryKey,
  queryFn: (csrfToken: string, requestParams: TRequestParams) => Promise<TQueryFnData>,
  options: UseQueryOptions<TQueryFnData, ServiceError, TData>,
  requestParams: TRequestParams,
  isSession?: boolean,
  isRedirect?: boolean
): UseCustomPostQueryReturn<TData, ServiceError>;

export function useCustomPostQuery<
  TQueryFnData = unknown,
  TRequestParams = unknown,
  TData = TQueryFnData
>(
  key: QueryKey,
  queryFn: (csrfToken: string, requestParams?: TRequestParams) => Promise<TQueryFnData>,
  options?: UseQueryOptions<TQueryFnData, ServiceError, TData>,
  requestParams?: TRequestParams,
  isSession = true,
  isRedirect = true
): UseCustomPostQueryReturn<TData, ServiceError> {
  const useUserDetailsQuery = useUserDetails();

  const csrfToken = useUserDetailsQuery.data?.data.csrfToken;

  // no need to explicitly throws error when get user details query failed, because our backend will throw error when there is no
  // csrf token in a POST request, hence the error will appear at postQuery
  const enhancedQueryFunction = useCallback((queryKey: any) => queryFn(csrfToken || '', requestParams), [
    queryFn,
    requestParams,
    csrfToken,
  ]);

  // invalidating 'key' will invalidate this post query
  // @ts-ignore
  const usePostQuery = useQuery([key, csrfToken], () => enhancedQueryFunction(csrfToken), {
    ...options,
    refetchInterval: undefined,
    enabled: getEnabledForPostQuery(!!csrfToken, useUserDetailsQuery.isError, options?.enabled),
  });

  const { refetch: postQueryRefetch } = usePostQuery;
  const { refetch: userDetailsRefetch, isError: userDetailsIsError } = useUserDetailsQuery;
  const [shouldRefetch, setShouldRefetch] = useState(0);
  useEffect(() => {
    // refetch only when user details query has returned with csrf
    // or when there is error with user details query
    if ((csrfToken || userDetailsIsError) && shouldRefetch) {
      postQueryRefetch();
    }
  }, [csrfToken, shouldRefetch, postQueryRefetch, userDetailsIsError]);

  // eslint-disable-next-line no-underscore-dangle
  const _refetch = useCallback(async () => {
    if (!csrfToken) {
      await userDetailsRefetch();
    }
    setShouldRefetch((prev) => prev + 1);
  }, [csrfToken, setShouldRefetch, userDetailsRefetch]);

  const error = useError();
  // eslint-disable-next-line no-underscore-dangle
  let _authorized = true;
  if (isSession) {
    // eslint-disable-next-line no-underscore-dangle
    _authorized = error.session(usePostQuery.error, isRedirect);
  }
  // eslint-disable-next-line no-underscore-dangle
  const _error = error.api(usePostQuery.error, isRedirect);

  return {
    ...usePostQuery,
    isFetching: useUserDetailsQuery.isFetching || usePostQuery.isFetching,
    _refetch,
    _error,
    _authorized,
  };
}

/**
 * Only trigger mutation if not mutate change, but trigger changed
 * @param mutateCallback useCallback with specific dependencies the mutation should rerun
 * @returns
 */
export const useTriggerMutate = (mutateCallback: () => void) => {
  const [triggerMutate, setTriggerMutate] = useState(0);
  const previousTriggerMutate = usePrevious(triggerMutate);

  useEffect(() => {
    if (previousTriggerMutate !== undefined && previousTriggerMutate !== triggerMutate) {
      mutateCallback();
    }
  }, [mutateCallback, previousTriggerMutate, triggerMutate]);

  return setTriggerMutate;
};
